import React, { useEffect, useState } from 'react';
import { CreditCard, FileText, Package, BookOpen, Loader2, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { trackDashboard, trackPageView } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';
import { billingService, BillingHistoryItem } from '@/services/billing.service';

const DashboardBilling: React.FC = () => {
  const [billingHistory, setBillingHistory] = useState<BillingHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useScrollTracking('DashboardBilling');

  useEffect(() => {
    trackDashboard.view('billing');
    trackPageView('DashboardBilling');
  }, []);

  useEffect(() => {
    loadBillingHistory();
  }, []);

  const loadBillingHistory = async () => {
    setLoading(true);
    setError(null);
    const result = await billingService.getBillingHistory();
    if (result.success && result.data) {
      setBillingHistory(result.data);
    } else {
      setError(result.error || 'Failed to load billing history');
    }
    setLoading(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(price);
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
            <CheckCircle className="w-3 h-3" />
            Active
          </span>
        );
      case 'refunded':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
            <RefreshCw className="w-3 h-3" />
            Refunded
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-400">
            <XCircle className="w-3 h-3" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-400">
            {status}
          </span>
        );
    }
  };

  const totalSpent = billingHistory
    .filter(item => item.status.toLowerCase() === 'active')
    .reduce((sum, item) => sum + item.purchase_price, 0);

  const activeCourses = billingHistory.filter(
    item => item.status.toLowerCase() === 'active' && item.product_type === 'course'
  ).length;

  const activeBundles = billingHistory.filter(
    item => item.status.toLowerCase() === 'active' && item.product_type === 'bundle'
  ).length;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
          <CreditCard className="w-10 h-10" />
          Billing & Subscriptions
        </h1>
        <p className="text-gray-300 text-lg">
          View your purchase history and enrolled products
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-slate-800/60 backdrop-blur-sm border border-indigo-500/30 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CreditCard className="w-5 h-5 text-green-400" />
            </div>
            <span className="text-gray-400 text-sm">Total Invested</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatPrice(totalSpent, 'usd')}</p>
        </div>

        <div className="bg-slate-800/60 backdrop-blur-sm border border-indigo-500/30 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-indigo-500/20 rounded-lg">
              <BookOpen className="w-5 h-5 text-indigo-400" />
            </div>
            <span className="text-gray-400 text-sm">Active Courses</span>
          </div>
          <p className="text-2xl font-bold text-white">{activeCourses}</p>
        </div>

        <div className="bg-slate-800/60 backdrop-blur-sm border border-indigo-500/30 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Package className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-gray-400 text-sm">Active Bundles</span>
          </div>
          <p className="text-2xl font-bold text-white">{activeBundles}</p>
        </div>
      </div>

      {/* Current Plan */}
      <div className="bg-slate-800/60 backdrop-blur-sm border-2 border-indigo-500/30 rounded-2xl p-8 mb-6">
        <h2 className="text-2xl font-bold text-white mb-6">Your Learning Plan</h2>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">
              {billingHistory.length > 0 ? 'Active Learner' : 'Free Plan'}
            </h3>
            <p className="text-gray-400">
              {billingHistory.length > 0
                ? `You have access to ${activeCourses} course${activeCourses !== 1 ? 's' : ''} and ${activeBundles} bundle${activeBundles !== 1 ? 's' : ''}`
                : 'Purchase courses to start your learning journey'}
            </p>
          </div>
          <a
            href="/courses"
            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg hover:shadow-indigo-500/50 transition-all"
          >
            Browse Courses
          </a>
        </div>
      </div>

      {/* Billing History */}
      <div className="bg-slate-800/60 backdrop-blur-sm border-2 border-indigo-500/30 rounded-2xl p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Purchase History</h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="text-red-400 mb-4">{error}</div>
            <button
              onClick={loadBillingHistory}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : billingHistory.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-500" />
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No Purchase History</h3>
            <p className="text-gray-400 mb-6">
              Your purchase history will appear here after you enroll in courses
            </p>
            <a
              href="/courses"
              className="inline-block px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg hover:shadow-indigo-500/50 transition-all"
            >
              Explore Courses
            </a>
          </div>
        ) : (
          <div className="space-y-4">
            {billingHistory.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-4 bg-slate-900/50 border border-white/10 rounded-lg hover:border-indigo-500/30 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${item.product_type === 'bundle' ? 'bg-purple-500/20' : 'bg-indigo-500/20'}`}>
                    {item.product_type === 'bundle' ? (
                      <Package className={`w-6 h-6 ${item.product_type === 'bundle' ? 'text-purple-400' : 'text-indigo-400'}`} />
                    ) : (
                      <BookOpen className="w-6 h-6 text-indigo-400" />
                    )}
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{item.product_title}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-gray-400 capitalize">{item.product_type}</span>
                      <span className="text-gray-600">•</span>
                      <span className="text-sm text-gray-400">{formatDate(item.enrolled_at)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {getStatusBadge(item.status)}
                  <span className="text-white font-semibold min-w-[80px] text-right">
                    {formatPrice(item.purchase_price, item.currency)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardBilling;
